<template>
<div class="container">
    <Breadcrumbs :here="pageTitle()"
                 :ancestries="ancestries()"/>
    <div class="row">
        <div class="col h2"
             :style="{ fontSize: FontSizes.large1}">{{ pageTitle() }}</div>
    </div>
    <div class="row justify-content-md-end mb-2">
        <button class="col col-md-4 btn btn-info"
                @click="onClickApproveByBranch">支店による承認処理</button>
    </div>
    <div class="row justify-content-between mb-2">
        <div class="col-md-4 col-8">
            <ClearableInput v-model:text="searchText"/>
        </div>
        <select class="month-select col-md-2 col-4 align-self-center"
                v-model="backwardMonthCountText">
            <option value="0">今月</option>
            <option value="1">前月</option>
        </select>
    </div>
    <div class="row my-3">
        <div class="col container">
            <div class="row approval-table-header-row">
                <div class="col-1"><span class="wrap-text">社員ID</span></div>
                <div class="col-2"><span class="wrap-text">社員名</span></div>
                <div class="col-6"><span class="wrap-text">メッセージ</span></div>
                <select class="col-3 mx-n1"
                        v-model="selectedAcceptState">
                    <option :value="acceptState.all">すべて表示</option>
                    <option :value="acceptState.accept">承認済み</option>
                    <option :value="acceptState.approvedByBranch">支店による承認済み</option>
                    <option :value="acceptState.notAccept">未承認</option>
                </select>
            </div>
            <InvoiceApprovalListCell v-for="apprv in searchedApprovals"
                                     :key="apprv.vForKey"
                                     :invoice-approval="apprv"
                                     :is-for-branch-page="true"/>
            <div v-if="searchedApprovals.length === 0"
                 class="row">
                <div class="col empty-table-text align-self-center my-5"
                     :style="{fontSize: FontSizes.large1}">表示するデータは存在しません</div>
            </div>
        </div>
    </div>
</div>
<BranchApprovalAffectedListDialog :show="showAffectedListDialog"
                                  :close="closeAffectedListDialog"
                                  :branch-name="branchName"
                                  :on-finish-approval="onFinishApprovalByBranch"/>
</template>

<script lang="ts">
import {defineComponent, onMounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {backendApi} from "@/utils/backend-api";
import {Staff} from "@/data-models/staff";
import {logoutMessages, subPaths} from "@/utils/consts";
import {loadingOverlayStateStore} from "@/stores/loading-overlay-state-store";
import ClearableInput from "@/components/ClearableInput.vue";
import {FontSizes} from "@/utils/font-sizes";
import Breadcrumbs, {ancestryType} from "@/components/Breadcrumbs.vue";
import {InvoiceApproval} from "@/data-models/invoice-approval";
import InvoiceApprovalListCell from "@/components/InvoiceApprovalListCell.vue";
import {confirmDialogStateStore} from "@/stores/confirm-dialog-state-store";
import BranchApprovalAffectedListDialog from "@/components/BranchApprovalAffectedListDialog.vue";

export default defineComponent({
    name: "ApprovalListForBranch",
    components: {BranchApprovalAffectedListDialog, InvoiceApprovalListCell, Breadcrumbs, ClearableInput},
    setup() {

        const acceptState = {
            all: "all",
            accept : "accept",
            notAccept: "not_accept",
            approvedByBranch: "approved_by_branch",
        }

        const route = useRoute(),
            router = useRouter()

        const  searchText = ref<string>(""),
            branchName = ref<string>(""),
            backwardMonthCountText = ref<string>("0"),
            searchedApprovals = ref<InvoiceApproval[]>([] as InvoiceApproval[]),
            selectedAcceptState = ref<string>(acceptState.all),
            showAffectedListDialog = ref<boolean>(false)

        let approvals = [] as InvoiceApproval[]

        const updateApprovals = async () => {
            // console.log(backwardMonthCountText.value)
            const resp = await backendApi.inquireApprovalListForBranch(branchName.value, parseInt(backwardMonthCountText.value))
            if (resp.isAuthorized) {
                approvals = InvoiceApproval.fromJsonArrToArr(resp.value)
                console.log(approvals)
            } else {
                await router.push({
                    path: subPaths.LOGGED_OUT,
                    query: {
                        logout_message: logoutMessages.TOKEN_EXPIRED.key
                    }
                })
            }
        }

        const updateSearchedApprovals = () => {
            searchedApprovals.value.splice(0)
            filterApprovalsByAcceptState().forEach(apprv => {
                if (searchText.value.length === 0) {
                    searchedApprovals.value.push(apprv)
                } else {
                    if (apprv.stringForSearch.includes(searchText.value)) {
                        searchedApprovals.value.push(apprv)
                    }
                }
            })
        }

        watch(() => backwardMonthCountText.value, async () => {
            await updateApprovals()
            updateSearchedApprovals()
        })

        watch(() => searchText.value, () => {
            updateSearchedApprovals()
        })

        watch(() => selectedAcceptState.value, () => {
            updateSearchedApprovals()
        })

        const filterApprovalsByAcceptState = (): InvoiceApproval[] => {
            const approvals2 = [] as InvoiceApproval[]
            approvals.forEach(apprv => {
                if (selectedAcceptState.value === acceptState.accept) {
                    if (apprv.accept && apprv.state === "") {
                        approvals2.push(apprv)
                    }
                } else if (selectedAcceptState.value === acceptState.notAccept) {
                    if (!apprv.accept) {
                        approvals2.push(apprv)
                    }
                } else if (selectedAcceptState.value === acceptState.approvedByBranch) {
                    if (apprv.accept && apprv.state === acceptState.approvedByBranch) {
                        approvals2.push(apprv)
                    }
                } else {
                    approvals2.push(apprv)
                }
            })
            return approvals2
        }


        onMounted(async () => {

            const branchNameQuery = route.query.name as string
            if (branchNameQuery) {

                branchName.value = branchNameQuery
                console.log(branchName.value)

                searchText.value = ""

                await updateApprovals()
                updateSearchedApprovals()

            }


            loadingOverlayStateStore.commit("fadeout")
        })

        const ancestries = (): ancestryType[] => {
            return [
                {
                    name: "ホーム",
                    pathObj: subPaths.HOME
                },
                {
                    name: branchName.value,
                    pathObj: {
                        path: subPaths.BRANCH_PAGE,
                        query: {
                            name: branchName.value
                        }
                    }
                },
            ]
        }

        const pageTitle = (): string => {
            return `${branchName.value}支店の請求承認メッセージ一覧`
        }

        const onClickApproveByBranch = () => {
            showAffectedListDialog.value = true
        }

        const closeAffectedListDialog = () => {
            showAffectedListDialog.value = false
        }

        const onFinishApprovalByBranch = async () => {
            await updateApprovals()
            updateSearchedApprovals()
            showAffectedListDialog.value = false
        }

        return {
            backwardMonthCountText,
            searchText,
            acceptState,
            selectedAcceptState,
            FontSizes,
            searchedApprovals,
            branchName,
            pageTitle,
            ancestries,
            onClickApproveByBranch,
            showAffectedListDialog,
            closeAffectedListDialog,
            onFinishApprovalByBranch,
        }
    }
})
</script>

<style scoped lang="scss">

.month-select {
    height: 30px;
}

.approval-table-header-row {
    height: 40px;
    font-weight: bold;

    border: 2px lightgray solid;

    select {
        border: none;
        height: 36px;
        border-left: 1px lightgray solid;
    }

    div {
        border-left: 1px lightgray solid;
    }
}

.empty-table-text {
    color: lightgray;
}


</style>